import { useEffect, useState } from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import {
  DOP_FIELDS,
  MANUFACTURER_LABELS,
  MANUFACTURER_REQUIRED,
  TECH_SYSTEM_KEYS,
  TECH_SYSTEM_KEY_LABELS,
  type TechField,
  type TechFieldType,
  type TechManufacturer,
} from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { api } from '@/lib/axios'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import {
  FeldStatus,
  FeldStatusZeichen,
  mitStand,
  useAenderung,
  useEntwurf,
  useFeld,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern'
import { bestaetige } from '@/lib/dialoge'

import { useTerminology } from '@/lib/useTerminology'
import { applyTechTemplate, fetchTechSchema, fetchTechTemplates, type TechTemplate } from '../api/project-tech.api'

/** `inlineInput` ohne `w-full` — sonst überstimmt es jede gesetzte Breite. */
const schmal = inlineInput.replace('w-full', '')

/**
 * AP-35 Detailblatt 6 2.4/2.5 — **Reiter „Technik & CE" der Einstellungen.**
 *
 * Drei Karten: Vorlage und Felder des Technik-Blatts, Herstellerdaten der
 * Leistungserklärung, Nummernmuster. Die Regeln des Schemas prüft der Server —
 * die Maske sperrt nur, was sie sicher weiß: Name und Typ eines gespeicherten
 * Feldes sind fest, und ein Feld mit Werten lässt sich nur ausblenden.
 *
 * AP-36 Phase D: ohne Knopf. Das Schema ist eine Liste in der Einstellungszeile
 * (Stelle `techSchema`): Jedes Feld ist ein Eintrag und speichert beim Verlassen
 * seiner Zeile als **Operation** auf dem aktuellen Stand (8.3 a), ebenso
 * Verschieben und Entfernen. Hersteller und Muster hängen an der Stelle
 * `projectSettings` (`manufacturer.*` einen Schlüssel tief, leer löscht).
 */
const TYPEN: { value: TechFieldType; label: string }[] = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Zahl' },
  { value: 'boolean', label: 'ja/nein' },
  { value: 'date', label: 'Datum' },
  { value: 'enum', label: 'Auswahl' },
  { value: 'user', label: 'Mitarbeiter' },
  { value: 'source', label: 'Quelle (Statik/Planung)' },
]

export function TechSettingsTab({ darfAendern, stelle }: { darfAendern: boolean; stelle: Speicherstelle<unknown> }) {
  return (
    <div className="space-y-4">
      <SchemaKarte darfAendern={darfAendern} />
      <HerstellerKarte darfAendern={darfAendern} stelle={stelle} />
    </div>
  )
}

/** Blatt 6 6. — die Meldung nennt das Feld, damit man es in der Tabelle findet. */
function schemaFehler(e: unknown): string | null {
  const d = (e as { response?: { data?: { code?: string; field?: string; count?: number } } })?.response?.data
  if (!d?.code) return null
  const f = d.field ?? ''
  switch (d.code) {
    case 'TECH_SYSTEM_KEY_DUPLICATE':
      return `Der Systemschlüssel „${TECH_SYSTEM_KEY_LABELS[f as keyof typeof TECH_SYSTEM_KEY_LABELS] ?? f}“ ist schon vergeben.`
    case 'TECH_SYSTEM_KEY_TYPE':
      return `Das Feld „${f}“ hat nicht den Typ, den sein Systemschlüssel verlangt.`
    case 'TECH_FIELD_IN_USE':
      return `Am Feld „${f}“ hängen ${d.count ?? 'mehrere'} Werte — ausblenden statt entfernen.`
    case 'TECH_TYPE_CHANGE':
      return `Der Typ des Feldes „${f}“ kann nicht geändert werden — ein neues Feld anlegen.`
    case 'TECH_FIELD_NAME_DUPLICATE':
      return `Den Namen „${f}“ gibt es zweimal.`
    case 'TECH_FIELD_INVALID':
      return `Das Feld „${f}“ ist unvollständig: Name aus Buchstaben und Ziffern, eine Beschriftung, bei „Auswahl“ mindestens ein Wert.`
    default:
      return null
  }
}

/** Die Felder aus dem Stand der Stelle (`{ schema: { fields }, inUse, updatedAt }`). */
const felderAus = (d: Datensatz): TechField[] => ((d.schema as { fields?: TechField[] } | undefined)?.fields ?? [])

function SchemaKarte({ darfAendern }: { darfAendern: boolean }) {
  const terms = useTerminology()
  const [geladen, setGeladen] = useState<Datensatz | null>(null)
  const [vorlagen, setVorlagen] = useState<TechTemplate[]>([])
  const [vorlage, setVorlage] = useState('EN1090')
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: 'techSchema',
    bezeichnung: 'Technik-Blatt',
    daten: geladen,
    schreibe: (patch, stand) => api.put<Datensatz>('/projects/settings/tech-schema', patch, mitStand(stand)).then((r) => r.data),
  }) as Speicherstelle<unknown>
  const d = useStellenDaten(stelle)
  const felder = felderAus(d)
  const inUse = (d.inUse as Record<string, number> | undefined) ?? {}
  const liste = useAenderung(stelle, 'techSchema:liste')

  useEffect(() => {
    Promise.all([fetchTechSchema(), fetchTechTemplates()])
      .then(([s, v]) => {
        setGeladen(s as unknown as Datensatz)
        setVorlagen(v)
      })
      .catch((e) => setFehler(apiErrorText(e, 'Das Technik-Blatt konnte nicht geladen werden.')))
  }, [])

  /** Die Liste als Operation auf dem aktuellen Stand — Verschieben, Entfernen, Anlegen. */
  const aendereListe = (neu: (l: TechField[]) => TechField[]) =>
    liste.sende({ felder: ['fields'], labels: { fields: 'Felder des Technik-Blatts' }, operation: (aktuell) => ({ fields: neu(felderAus(aktuell)) }) })

  const schiebe = (name: string, richtung: -1 | 1) =>
    void aendereListe((l) => {
      const n = [...l]
      const i = n.findIndex((f) => f.name === name)
      const z = i + richtung
      if (i < 0 || z < 0 || z >= n.length) return l
      ;[n[i], n[z]] = [n[z], n[i]]
      return n
    })

  return (
    <div className={`${formCardSm} space-y-3`}>
      <h3 className={cardTitle}>Technik-Blatt</h3>
      <p className="text-sm text-muted-foreground">
        Die Felder stehen am {terms.project} als Vorgabe und an jeder {terms.position} als Abweichung. Die Anwendung
        reagiert nur auf den <em>Systemschlüssel</em> — die Beschriftung ist frei. Eine Zeile speichert, sobald du sie verlässt.
      </p>

      {darfAendern && (
        <div className="flex flex-wrap items-center gap-2">
          <select className={`${schmal} w-64`} value={vorlage} onChange={(e) => setVorlage(e.target.value)} aria-label="Vorlage">
            {vorlagen.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name} ({v.fieldCount} Felder)
              </option>
            ))}
          </select>
          <button
            type="button"
            className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
            onClick={async () => {
              setFehler(null)
              setHinweis(null)
              try {
                stelle.uebernimm(await applyTechTemplate(vorlage))
                setHinweis('Vorlage übernommen — Felder mit Werten sind ausgeblendet erhalten.')
              } catch (e) {
                setFehler(schemaFehler(e) ?? apiErrorText(e, 'Das hat nicht geklappt.'))
              }
            }}
          >
            Vorlage übernehmen
          </button>
          <span className="text-xs text-muted-foreground">ersetzt die Felder; was schon Werte trägt, bleibt ausgeblendet</span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-muted-foreground">
            <tr>
              <th className="px-1 py-1 text-left">Beschriftung</th>
              <th className="px-1 py-1 text-left">Name</th>
              <th className="px-1 py-1 text-left">Typ</th>
              <th className="px-1 py-1 text-left">Abschnitt</th>
              <th className="px-1 py-1 text-left">Auswahlwerte</th>
              <th className="px-1 py-1 text-left">Systemschlüssel</th>
              <th className="px-1 py-1 text-left">Erklärung</th>
              <th className="px-1 py-1 text-center" title="Pflicht">Pfl.</th>
              <th className="px-1 py-1 text-center" title="in der Mitarbeiter-App">App</th>
              <th className="px-1 py-1 text-center" title="ausgeblendet">aus</th>
              <th className="px-1 py-1" />
            </tr>
          </thead>
          <tbody>
            {felder.map((f) => (
              <SchemaZeile
                key={f.name}
                stelle={stelle}
                feld={f}
                werte={inUse[f.name] ?? 0}
                darf={darfAendern}
                onSchiebe={(r) => schiebe(f.name, r)}
                onEntfernen={async () => {
                  if (!(await bestaetige(`Feld „${f.label}" entfernen?`, { ok: 'Entfernen', gefaehrlich: true }))) return
                  void aendereListe((l) => l.filter((x) => x.name !== f.name))
                }}
              />
            ))}
            {felder.length === 0 && (
              <tr>
                <td colSpan={11} className="px-1 py-4 text-center text-muted-foreground">
                  Kein Feld — eine Vorlage übernehmen oder Felder anlegen.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <FeldStatus status={liste.status} />

      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      {hinweis && <p className="text-sm text-green-700 dark:text-green-400">{hinweis}</p>}

      {darfAendern && <NeuesFeld vorhanden={felder} onAnlegen={(f) => aendereListe((l) => [...l, f])} />}
    </div>
  )
}

/** Eine Zeile des Schemas — ein Eintrag, gespeichert beim Verlassen der Zeile. */
function SchemaZeile({
  stelle,
  feld,
  werte,
  darf,
  onSchiebe,
  onEntfernen,
}: {
  stelle: Speicherstelle<unknown>
  feld: TechField
  werte: number
  darf: boolean
  onSchiebe: (richtung: -1 | 1) => void
  onEntfernen: () => void
}) {
  const terms = useTerminology()
  const eintrag = useEntwurf<TechField>(stelle, {
    kennung: `techField:${feld.name}`,
    gespeichert: feld,
    felder: ['fields'],
    label: feld.label,
    gesperrt: !darf,
    pruefe: (f) => (!f.label.trim() ? 'Die Beschriftung fehlt.' : f.type === 'enum' && !(f.options ?? []).some((o) => o.trim()) ? 'Eine Auswahl braucht mindestens einen Wert.' : null),
    operation: (neu) => (aktuell) => ({
      fields: felderAus(aktuell).map((x) => (x.name === feld.name ? { ...neu, options: neu.options?.map((o) => o.trim()).filter(Boolean) } : x)),
    }),
  })
  const f = eintrag.entwurf ?? feld
  const aendere = (teil: Partial<TechField>) => eintrag.setze((x) => ({ ...x, ...teil }))

  return (
    <>
      <tr className={`border-t border-border ${f.hidden ? 'opacity-60' : ''}`} {...eintrag.huelle}>
        <td className="px-1 py-1">
          <input className={inlineInput} value={f.label} disabled={!darf} onChange={(e) => aendere({ label: e.target.value })} />
        </td>
        <td className="px-1 py-1">
          <input className={`${schmal} w-32`} value={f.name} disabled title="Der Name trägt die Werte und bleibt fest." readOnly />
        </td>
        <td className="px-1 py-1">
          <select className={`${schmal} w-28`} value={f.type} disabled title="Der Typ eines Feldes bleibt — für einen anderen Typ ein neues Feld anlegen.">
            {TYPEN.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </td>
        <td className="px-1 py-1">
          <input className={`${schmal} w-28`} value={f.section ?? ''} disabled={!darf} onChange={(e) => aendere({ section: e.target.value })} />
        </td>
        <td className="px-1 py-1">
          {f.type === 'enum' || f.type === 'string' || f.type === 'user' ? (
            // enum: erlaubte Werte · string: Vorschläge (frei tippbar) · user: Projektrolle zum Vorfiltern (Nachtrag 4)
            <span className="flex items-center gap-1">
              <input
                className={`${schmal} w-40`}
                value={(f.options ?? []).join('; ')}
                disabled={!darf}
                placeholder={f.type === 'enum' ? 'A; B; C' : f.type === 'user' ? 'Projektrolle' : 'Vorschläge A; B'}
                title={
                  f.type === 'enum'
                    ? 'Nur diese Werte sind erlaubt.'
                    : f.type === 'user'
                      ? 'Projektrolle aus der Mitarbeiterverwaltung, z. B. FPC oder WELDING_COORDINATOR'
                      : 'Vorschläge — frei tippen bleibt möglich.'
                }
                onChange={(e) => aendere({ options: e.target.value.split(';').map((o) => o.trimStart()) })}
              />
              {f.type === 'string' && (
                <label className="flex items-center gap-0.5 text-[11px] text-muted-foreground" title="mehrzeilig">
                  <input type="checkbox" checked={!!f.multiline} disabled={!darf} onChange={(e) => aendere({ multiline: e.target.checked })} />
                  ¶
                </label>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-1 py-1">
          <select
            className={`${schmal} w-40`}
            value={f.systemKey ?? ''}
            disabled={!darf}
            onChange={(e) => aendere({ systemKey: (e.target.value || null) as TechField['systemKey'] })}
          >
            <option value="">—</option>
            {(Object.keys(TECH_SYSTEM_KEYS) as (keyof typeof TECH_SYSTEM_KEYS)[])
              .filter((k) => TECH_SYSTEM_KEYS[k] === f.type)
              .map((k) => (
                <option key={k} value={k}>
                  {TECH_SYSTEM_KEY_LABELS[k]}
                </option>
              ))}
          </select>
        </td>
        <td className="px-1 py-1">
          <select
            className={`${schmal} w-40`}
            value={f.dopField ?? ''}
            disabled={!darf}
            onChange={(e) => aendere({ dopField: (e.target.value || null) as TechField['dopField'] })}
          >
            <option value="">—</option>
            {(Object.keys(DOP_FIELDS) as (keyof typeof DOP_FIELDS)[]).map((k) => (
              <option key={k} value={k}>
                {DOP_FIELDS[k].label}
              </option>
            ))}
          </select>
        </td>
        <td className="px-1 py-1 text-center">
          <input type="checkbox" checked={!!f.required} disabled={!darf} onChange={(e) => aendere({ required: e.target.checked })} />
        </td>
        <td className="px-1 py-1 text-center">
          <input type="checkbox" checked={!!f.showInPwa} disabled={!darf} onChange={(e) => aendere({ showInPwa: e.target.checked })} />
        </td>
        <td className="px-1 py-1 text-center">
          <input type="checkbox" checked={!!f.hidden} disabled={!darf} onChange={(e) => aendere({ hidden: e.target.checked })} />
        </td>
        <td className="whitespace-nowrap px-1 py-1 text-right">
          <FeldStatusZeichen status={eintrag} />
          {darf && (
            <>
              <button type="button" className="p-0.5 text-muted-foreground hover:text-foreground" onClick={() => onSchiebe(-1)} aria-label="nach oben">
                <ArrowUp size={14} />
              </button>
              <button type="button" className="p-0.5 text-muted-foreground hover:text-foreground" onClick={() => onSchiebe(1)} aria-label="nach unten">
                <ArrowDown size={14} />
              </button>
              <button
                type="button"
                className="p-0.5 text-muted-foreground hover:text-destructive disabled:opacity-30"
                disabled={werte > 0}
                title={werte > 0 ? `${werte} ${terms.projectPlural} bzw. ${terms.positionPlural} tragen einen Wert — ausblenden statt entfernen.` : 'entfernen'}
                onClick={onEntfernen}
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
        </td>
      </tr>
      {eintrag.fehler && (
        <tr>
          <td colSpan={11} className="px-1 pb-1">
            <FeldStatus status={eintrag} zeichen={false} />
          </td>
        </tr>
      )}
    </>
  )
}

/** Ein neues Feld: Beschriftung, Name und Typ — Name und Typ stehen danach fest (Fall 2). */
function NeuesFeld({ vorhanden, onAnlegen }: { vorhanden: TechField[]; onAnlegen: (f: TechField) => Promise<unknown> }) {
  const leer = { label: '', name: '', type: 'string' as TechFieldType }
  const [neu, setNeu] = useState(leer)
  const nameFrei = neu.name && !vorhanden.some((f) => f.name === neu.name)
  const gueltig = neu.label.trim() && /^[A-Za-z][A-Za-z0-9]*$/.test(neu.name) && nameFrei
  return (
    <div className="flex flex-wrap items-end gap-2">
      <input className={`${schmal} w-48`} placeholder="Beschriftung" value={neu.label} onChange={(e) => setNeu({ ...neu, label: e.target.value })} />
      <input
        className={`${schmal} w-32`}
        placeholder="Name (z. B. feld7)"
        value={neu.name}
        onChange={(e) => setNeu({ ...neu, name: e.target.value.replace(/[^A-Za-z0-9]/g, '') })}
      />
      <select className={`${schmal} w-28`} value={neu.type} onChange={(e) => setNeu({ ...neu, type: e.target.value as TechFieldType })}>
        {TYPEN.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={!gueltig}
        className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
        onClick={async () => {
          await onAnlegen({ name: neu.name, label: neu.label.trim(), type: neu.type, ...(neu.type === 'enum' ? { options: ['A'] } : {}) })
          setNeu(leer)
        }}
      >
        <Plus size={14} /> Feld anlegen
      </button>
      {neu.name && !nameFrei && <span className="text-xs text-destructive">Den Namen gibt es schon.</span>}
    </div>
  )
}

const HERSTELLER_FELDER: (keyof TechManufacturer)[] = [
  'name',
  'address',
  'plant',
  'fpcCertificateNumber',
  'fpcCertificateDate',
  'notifiedBodyName',
  'notifiedBodyNumber',
  'weldingCoordinatorName',
  'weldingCoordinatorQualification',
  'weldingCoordinatorCertificate',
  'signatoryName',
  'signatoryFunction',
  'signatoryPlace',
]

/** Hersteller und Muster — Felder der Stelle `projectSettings` (`manufacturer.*` einen Schlüssel tief). */
function HerstellerKarte({ darfAendern, stelle }: { darfAendern: boolean; stelle: Speicherstelle<unknown> }) {
  const sprache = useFeld(stelle, 'manufacturer.language', { art: 'auswahl', label: MANUFACTURER_LABELS.language, gesperrt: !darfAendern })
  const muster = useFeld(stelle, 'declarationNumberPattern', {
    label: 'Nummernmuster der Erklärung',
    pflicht: true,
    gesperrt: !darfAendern,
    pruefe: (w) => (w && !w.includes('{nummer}') ? 'Das Muster braucht {nummer}.' : null),
  })

  return (
    <div className={`${formCardSm} space-y-3`}>
      <h3 className={cardTitle}>Hersteller und Leistungserklärung</h3>
      <p className="text-sm text-muted-foreground">
        Diese Angaben stehen auf jeder Leistungserklärung und jedem CE-Etikett. Pflicht für das Ausstellen:{' '}
        {MANUFACTURER_REQUIRED.map((k) => MANUFACTURER_LABELS[k]).join(', ')}. Bei EXC3/EXC4 zusätzlich Schweißaufsicht
        mit Zertifikat. Die Zertifizierungsstelle bestätigt die Vollständigkeit — die Vorlage ist kein Rechtsrat.
      </p>
      <div className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
        {HERSTELLER_FELDER.map((k) => (
          <HerstellerFeld key={k} stelle={stelle} k={k} darf={darfAendern} />
        ))}
        <label className="block text-xs text-muted-foreground">
          {MANUFACTURER_LABELS.language}
          <select className={inlineInput} {...sprache.props} value={sprache.props.value || 'DE'}>
            <option value="DE">Deutsch</option>
            <option value="EN">Englisch</option>
          </select>
          <FeldStatus status={sprache} />
        </label>
        <label className="block text-xs text-muted-foreground">
          Nummernmuster der Erklärung
          <input className={inlineInput} {...muster.props} />
          <span className="text-[11px]">Platzhalter {'{jahr}'}, {'{jahr2}'}, {'{nummer}'} (Pflicht)</span>
          <FeldStatus status={muster} />
        </label>
      </div>
    </div>
  )
}

/** Eine Herstellerangabe — leer löscht sie (der Server entfernt den Schlüssel). */
function HerstellerFeld({ stelle, k, darf }: { stelle: Speicherstelle<unknown>; k: keyof TechManufacturer; darf: boolean }) {
  const f = useFeld(stelle, `manufacturer.${k}`, { label: MANUFACTURER_LABELS[k], gesperrt: !darf })
  return (
    <label className="block text-xs text-muted-foreground">
      <span className="flex items-center gap-1">
        {MANUFACTURER_LABELS[k]}
        {MANUFACTURER_REQUIRED.includes(k) && ' *'}
        <FeldStatusZeichen status={f} />
      </span>
      {k === 'address' ? <textarea className={inlineInput} rows={2} {...f.props} /> : <input className={inlineInput} {...f.props} />}
      <FeldStatus status={f} zeichen={false} />
    </label>
  )
}
